import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

export enum LoginStatus {
  InProgress = 0,
  LoggedIn = 1,
  LoggedOut = 2,
}

const STORAGE_KEYS = {
  loginStatus: 'loginStatus',
  hasTools: 'hasTools',
  userName: 'userName',
  explicitLogout: 'explicitLogout',
} as const

@Injectable({
  providedIn: 'root',
})
export class UserService {
  loginStatus$ = new BehaviorSubject<LoginStatus>(LoginStatus.LoggedOut)
  hasTools$ = new BehaviorSubject<boolean>(false)
  userName$ = new BehaviorSubject<string>('')

  constructor() {
    const currentValue = this.safeGetItem(STORAGE_KEYS.loginStatus)

    if (currentValue == null) {
      this.loginStatus$.next(LoginStatus.LoggedOut)
    } else {
      const loggedIn = parseInt(currentValue) as LoginStatus
      this.loginStatus$.next(loggedIn)
    }

    // Rehydrate hasTools/userName from localStorage too, otherwise a page
    // reload or new tab (no fresh LOGIN_SUCCESS event) leaves these subjects
    // stuck at their hardcoded defaults even though loginStatus is correct
    this.hasTools$.next(this.safeGetItem(STORAGE_KEYS.hasTools) === 'true')
    this.userName$.next(
      this.readStoredUserName(this.safeGetItem(STORAGE_KEYS.userName)),
    )

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.loginStatus) {
        this.loginStatus$.next(
          event.newValue == null
            ? LoginStatus.LoggedOut
            : (parseInt(event.newValue) as LoginStatus),
        )
      } else if (event.key === STORAGE_KEYS.hasTools) {
        this.hasTools$.next(event.newValue === 'true')
      } else if (event.key === STORAGE_KEYS.userName) {
        this.userName$.next(this.readStoredUserName(event.newValue))
      } else if (event.key === null) {
        this.loginStatus$.next(LoginStatus.LoggedOut)
        this.hasTools$.next(false)
        this.userName$.next('')
      }
    })
  }

  IsLoggedIn(): boolean {
    const currentValue = this.safeGetItem(STORAGE_KEYS.loginStatus)
    if (currentValue == null) {
      this.loginStatus$.next(LoginStatus.LoggedOut)
      return this.loginStatus$.value == LoginStatus.LoggedIn
    }
    const loggedIn = parseInt(currentValue) as LoginStatus
    this.loginStatus$.next(loggedIn)
    return this.loginStatus$.value == LoginStatus.LoggedIn
  }

  GetLoginStatus(): LoginStatus {
    const loginStatus = this.safeGetItem(STORAGE_KEYS.loginStatus)
    if (loginStatus == null) {
      return LoginStatus.LoggedOut
    }
    const loggedIn = parseInt(loginStatus) as LoginStatus
    this.loginStatus$.next(loggedIn)
    return this.loginStatus$.getValue()
  }

  // hastools
  SetLoggedIn(loggedIn: LoginStatus) {
    localStorage.setItem(STORAGE_KEYS.loginStatus, loggedIn.toString())
    this.loginStatus$.next(loggedIn)
  }

  SetHasTools(hasTools: boolean) {
    localStorage.setItem(STORAGE_KEYS.hasTools, hasTools.toString())
    this.hasTools$.next(hasTools)
  }

  // Clears session-derived state on logout so a subsequent page load doesn't
  // read stale "logged in" flags before MSAL's own account cache has caught up
  ClearSession() {
    localStorage.removeItem(STORAGE_KEYS.hasTools)
    localStorage.removeItem(STORAGE_KEYS.userName)
    this.hasTools$.next(false)
    this.userName$.next('')
    this.SetLoggedIn(LoginStatus.LoggedOut)
  }

  // Marks that the user actively chose to log out, so the next login attempt
  // forces the Entra account picker instead of silently re-using an SSO
  // session for the same account
  SetExplicitLogout() {
    localStorage.setItem(STORAGE_KEYS.explicitLogout, 'true')
  }

  // Reads and clears the explicit-logout flag so it only affects the very
  // next login attempt, not every subsequent one
  ConsumeExplicitLogout(): boolean {
    const wasExplicit = this.safeGetItem(STORAGE_KEYS.explicitLogout) === 'true'
    localStorage.removeItem(STORAGE_KEYS.explicitLogout)
    return wasExplicit
  }

  HasTools(): boolean {
    const hasTools = this.safeGetItem(STORAGE_KEYS.hasTools)
    if (hasTools == null) {
      return false
    }
    this.hasTools$.next(hasTools == 'true')
    return this.hasTools$.getValue()
  }

  // Username
  SetUserName(userName: string) {
    localStorage.setItem(STORAGE_KEYS.userName, userName)
    this.userName$.next(userName)
  }

  GetUserName(): string {
    return this.readStoredUserName(this.safeGetItem(STORAGE_KEYS.userName))
  }

  // Treats a stringified 'null'/'undefined' (e.g. from a past setItem(key, String(x)) bug)
  // the same as an absent value, instead of rendering it as a literal username
  private readStoredUserName(raw: string | null): string {
    return raw && raw !== 'null' && raw !== 'undefined' ? raw : ''
  }

  // localStorage can throw (private browsing, storage disabled, quota errors on some
  // browsers) — swallow it here so a read failure degrades to "logged out" instead of
  // crashing this root-provided singleton's constructor and the whole app with it
  private safeGetItem(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }

  getPreferredUsername(): string {
    // Retrieve the key associated with the account information
    const accountKeys = localStorage.getItem('msal.account.keys')
    if (!accountKeys) {
      console.error('Account keys not found')
      return ''
    }

    // Parse the JSON string to get the array of keys
    const keysArray = JSON.parse(accountKeys)
    if (!keysArray || keysArray.length === 0) {
      console.error('Account keys array is empty or invalid')
      return ''
    }

    // Use the first key from the array to retrieve the specific account object
    // You might need to adjust this if you need to select a different key
    const accountValue = localStorage.getItem(keysArray[0])
    if (!accountValue) {
      console.error('Account object not found')
      return ''
    }

    // Parse the account object
    const accountObj = JSON.parse(accountValue)
    if (!accountObj || !accountObj.username) {
      console.error('Invalid account object or accountObj.username not found')
      return ''
    }

    // Retrieve the preferred_username from idTokenClaims
    return accountObj.username
  }
}
